import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import { CUSTOMER_ACCOUNT } from 'Component/Header/Header.config';
// import MyAccountQuery from 'Query/MyAccount.query';
import OrderQuery from 'Query/Order.query';
import {
    mapDispatchToProps as sourceMapDispatchToProps,
    mapStateToProps as sourceMapStateToProps,
    MyAccountContainer as SourceMyAccountContainer
} from 'SourceRoute/MyAccount/MyAccount.container';
import { toggleBreadcrumbs } from 'Store/Breadcrumbs/Breadcrumbs.action';
import OrderReducer from 'Store/Order/Order.reducer';
import {
    ACCOUNT_INFORMATION,
    ADDRESS_BOOK, CUSTOMER_SERVICE,
    CustomerType,
    FIRST_SECTION,
    MY_ACCOUNT, MY_DOCUMENTS, MY_DOWNLOADABLE, MY_ORDERS, MY_PROFILE,
    MY_WISHLIST, NEWSLETTER_SUBSCRIPTION, QUICK_ORDER,
    SECOND_SECTION, THIRD_SECTION
} from 'Type/Account.type';
import { isSignedIn } from 'Util/Auth';
import { scrollToTop } from 'Util/Browser';
import { withReducers } from 'Util/DynamicReducer';
import history from 'Util/History';
import { fetchQuery } from 'Util/Request';
import { appendWithStoreCode, replace } from 'Util/Url';

import MyAccount from './MyAccount.component';
import {
    ACCOUNT_CUSTOMER_SERVICE_ISSUE_URL,
    ACCOUNT_DOCUMENTS_URL,
    ACCOUNT_LOGIN_URL,
    ACCOUNT_ORDER_FAVORITES_URL,
    ACCOUNT_QUICK_ORDER_URL,
    ACCOUNT_URL,
    GROUP_ID_EMPLOYEE,
    GROUP_ID_PRO, GROUP_ID_STUDENT, PENDING_STATUSES
} from './MyAccount.config';

/** @namespace PeggysagePwa/Route/MyAccount/Container/mapStateToProps */
export const mapStateToProps = (state) => ({
    ...sourceMapStateToProps(state),
    customer: state.MyAccountReducer.customer
});

/** @namespace PeggysagePwa/Route/MyAccount/Container/mapDispatchToProps */
export const mapDispatchToProps = (dispatch) => ({
    ...sourceMapDispatchToProps(dispatch),
    toggleBreadcrumbs: (isVisible) => dispatch(toggleBreadcrumbs(isVisible))
});

/** @namespace PeggysagePwa/Route/MyAccount/Container */
export class MyAccountContainer extends SourceMyAccountContainer {
    static propTypes = {
        ...SourceMyAccountContainer.propTypes,
        customer: CustomerType.isRequired,
        toggleBreadcrumbs: PropTypes.func.isRequired
    };

    static tabMap = {
        [MY_ACCOUNT]: {
            url: '',
            tabName: __('My Account'),
            title: __('Bienvenue sur votre compte !'),
            section: FIRST_SECTION
        },
        [MY_ORDERS]: {
            url: '/sales/order/history',
            tabName: __('My Orders'),
            section: FIRST_SECTION,
            isFullUrl: true
        },
        [QUICK_ORDER]: {
            url: '/quick-order',
            tabName: __('Quick order'),
            section: FIRST_SECTION,
            isFullUrl: true
        },
        [MY_DOWNLOADABLE]: {
            url: '/downloadable/customer/products',
            tabName: __('My Downloadable'),
            section: FIRST_SECTION,
            isFullUrl: true
        },
        [MY_WISHLIST]: {
            url: '/wishlist',
            tabName: __('My Wish List'),
            section: FIRST_SECTION,
            isFullUrl: true
        },
        [MY_PROFILE]: {
            tabName: __('My profile'),
            section: SECOND_SECTION,
            hasChildren: true
        },
        [ACCOUNT_INFORMATION]: {
            parent: MY_PROFILE,
            url: '/edit',
            tabName: __('My personal information'),
            // title: __('Edit Account Information'),
            section: SECOND_SECTION
        },
        [ADDRESS_BOOK]: {
            parent: MY_PROFILE,
            url: '/customer/address',
            tabName: __('My addresses'),
            section: SECOND_SECTION,
            isFullUrl: true
        },
        [NEWSLETTER_SUBSCRIPTION]: {
            parent: MY_PROFILE,
            url: '/newsletter/manage',
            tabName: __('My subscriptions'),
            section: SECOND_SECTION,
            isFullUrl: true
        },
        [MY_DOCUMENTS]: {
            parent: MY_PROFILE,
            url: ACCOUNT_DOCUMENTS_URL,
            tabName: __('My documents'),
            section: SECOND_SECTION,
            isFullUrl: true
        },
        [CUSTOMER_SERVICE]: {
            url: '/customer/issues',
            tabName: __('Customer service'),
            section: THIRD_SECTION,
            isFullUrl: true
        }
    };

    /**
     * Override: disable My Downloadable (/!\ also in non-static method!)
     * @param tabName
     * @returns {boolean|*}
     */
    static isTabEnabled(props, tabName) {
        const {
            isWishlistEnabled,
            newsletterActive,
            customer: {
                group_id = 0,
                status_pro: accountStatus
            } = {}
        } = props;

        switch (tabName) {
        case MY_WISHLIST:
            return isWishlistEnabled;
        case NEWSLETTER_SUBSCRIPTION:
            return newsletterActive;
        case MY_DOWNLOADABLE:
            return false;
        case QUICK_ORDER:
            return [GROUP_ID_PRO, GROUP_ID_STUDENT, GROUP_ID_EMPLOYEE].includes(group_id);
        case MY_DOCUMENTS:
            return [GROUP_ID_PRO, GROUP_ID_STUDENT, GROUP_ID_EMPLOYEE].includes(group_id) || PENDING_STATUSES.includes(accountStatus);
        default:
            return true;
        }
    }

    /**
     * Override: duplicate of parent method because of static method call
     * @param props
     * @param state
     * @returns {null|{activeTab: (*|string)}|{activeTab}}
     */
    static navigateToSelectedTab(props, state = {}) {
        const {
            match: {
                params: {
                    tab: historyActiveTab
                } = {}
            } = {},
            isMobile,
            selectedTab
        } = props;
        const { activeTab } = state;

        if (this.tabMap[selectedTab] && isSignedIn()) {
            return { activeTab: selectedTab };
        }

        // redirect to Dashboard, if user visited non-existent or disabled page
        const newActiveTab = this.tabMap[historyActiveTab] && MyAccountContainer.isTabEnabled(props, historyActiveTab)
            ? historyActiveTab
            : MY_ACCOUNT;
        const { url: activeTabUrl } = this.tabMap[newActiveTab];

        if (historyActiveTab !== newActiveTab && activeTab !== MY_ACCOUNT && isSignedIn() && !isMobile) {
            history.push(appendWithStoreCode(`${ ACCOUNT_URL }${ activeTabUrl }`));
        }

        if (activeTab !== newActiveTab) {
            return { activeTab: newActiveTab };
        }

        return null;
    }

    containerFunctions = {
        ...this.containerFunctions,
        onBackClick: this.onBackClick.bind(this)
    };

    /**
     * Override: disable breadcrumbs on mobile, check customer orders for redirect
     * @param props
     * @private
     */
    __construct() {
        const { isMobile, toggleBreadcrumbs } = this.props;

        if (isMobile) {
            toggleBreadcrumbs(false);
        }

        const {
            updateMeta,
            toggleOverlayByKey
        } = this.props;

        this.state = {
            ...MyAccountContainer.navigateToSelectedTab(this.props),
            isEditingActive: false,
            tabName: '',
            stateSubHeading: '',
            hasOrders: null,
            isLoadingOrders: false
        };

        if (!isSignedIn()) {
            toggleOverlayByKey(CUSTOMER_ACCOUNT);
        }

        updateMeta({ title: __('My account') });

        this.checkCustomerOrder();

        this.redirectIfNotSignedIn();
        this.onSignIn();
        this.updateBreadcrumbs();
        scrollToTop();
    }

    /**
     * Override: duplicate of parent method because of static method call
     * @param props
     * @param state
     * @returns {{activeTab: *}|{activeTab: *}}
     */
    static getDerivedStateFromProps(props, state) {
        return MyAccountContainer.navigateToSelectedTab(props, state);
    }

    /**
     * Override: update breadcrumb visibility
     * @param prevProps
     * @param prevState
     */
    componentDidUpdate(prevProps, prevState) {
        const { isMobile, toggleBreadcrumbs } = this.props;
        const { isMobile: prevIsMobile } = prevProps;

        const { activeTab: prevActiveTab } = prevState;
        const { activeTab } = this.state;

        if (activeTab !== prevActiveTab) {
            this.checkCustomerOrder();
        }

        if (isMobile !== prevIsMobile) {
            toggleBreadcrumbs(!isMobile);
            this.updateBreadcrumbs();
        }

        super.componentDidUpdate(prevProps, prevState);
    }

    goToAccountInformationTab() {
        const { url: accountInformationTabUrl } = MyAccountContainer.tabMap[ACCOUNT_INFORMATION];
        history.push(appendWithStoreCode(`${ ACCOUNT_URL }${ accountInformationTabUrl }`));
    }

    async checkCustomerOrder() {
        const { customer: { group_id: customerGroupId }, isMobile } = this.props;
        const { activeTab, isLoadingOrders, hasOrders } = this.state;

        if (isMobile || isLoadingOrders || hasOrders || activeTab !== MY_ACCOUNT) {
            return;
        }

        if (hasOrders === 0 && customerGroupId === 1) {
            this.goToAccountInformationTab();
            return;
        }

        this.setState({ isLoadingOrders: true });

        fetchQuery(OrderQuery.getOrderListQuery({ page: 1, pageSize: 1 })).then(
            /** @namespace PeggysagePwa/Route/MyAccount/Container/MyAccountContainer/checkCustomerOrder/then/catch/fetchQuery/then */
            ({ customer: { orders: { total_count } } }) => {
                this.setState({ isLoadingOrders: false, hasOrders: +total_count }, () => {
                    if (!isMobile && !total_count && customerGroupId === 1) {
                        this.goToAccountInformationTab();
                    }
                });
            }
        ).catch(
            /** @namespace PeggysagePwa/Route/MyAccount/Container/MyAccountContainer/checkCustomerOrder/then/catch */
            () => {
                this.setState({ isLoadingOrders: false, hasOrders: false });
            }
        );

        // fetchQuery(MyAccountQuery.getLastCurrentOrder()).then(
        //     /** @namespace PeggysagePwa/Route/MyAccount/Container/MyAccountContainer/checkCustomerOrder/then/catch/fetchQuery/then */
        //     ({ getLastCurrentOrder: order }) => {
        //         this.setState({ isLoadingOrders: false, hasOrders: !!order });
        //         if (!isMobile && !order && customerGroupId === 1) {
        //             this.goToAccountInformationTab();
        //         }
        //     }
        // ).catch(
        //     /** @namespace PeggysagePwa/Route/MyAccount/Container/MyAccountContainer/checkCustomerOrder/then/catch */
        //     () => {
        //         this.setState({ isLoadingOrders: false, hasOrders: false });
        //     }
        // );
    }

    /**
     * Override: get order history tabname for favorites order page
     * @returns {string|*}
     */
    getTabName() {
        const { location: { pathname } } = this.props;
        const { tabName: stateTabName, activeTab } = this.state;
        const { tabName, url } = MyAccountContainer.tabMap[activeTab];

        if (activeTab === CUSTOMER_SERVICE && pathname.includes(ACCOUNT_CUSTOMER_SERVICE_ISSUE_URL)) {
            return __('Ma demande');
        }

        if (activeTab === MY_ORDERS && pathname.includes(ACCOUNT_ORDER_FAVORITES_URL)) {
            return tabName;
        }

        if (!pathname.includes(url)) {
            return stateTabName;
        }

        return tabName;
    }

    /**
     * Override: disable My Downloadable (/!\ also in static method!)
     * @param tabName
     * @returns {boolean|*}
     */
    isTabEnabled(tabName) {
        const { customer: { group_id = 0, status_pro: accountStatus } = {} } = this.props;

        switch (tabName) {
        case MY_DOWNLOADABLE:
            return false;
        case QUICK_ORDER:
            return [GROUP_ID_PRO, GROUP_ID_STUDENT, GROUP_ID_EMPLOYEE].includes(group_id);
        case MY_DOCUMENTS:
            return [GROUP_ID_PRO, GROUP_ID_STUDENT, GROUP_ID_EMPLOYEE].includes(group_id) || PENDING_STATUSES.includes(accountStatus);
        default:
            return super.isTabEnabled(tabName);
        }
    }

    /**
     * Override: duplicate of parent method because of static method call
     * @returns {{[p: string]: {tabName: *, section: (number|*), url: string}}}
     */
    tabsFilterEnabled() {
        return Object.entries(MyAccountContainer.tabMap).reduce((enabledTabs, [key, value]) => (
            MyAccountContainer.isTabEnabled(this.props, key)
                ? { ...enabledTabs, [key]: value } : enabledTabs
        ), {});
    }

    /**
     * Override: duplicate of parent method because of static method call
     * @param activeTab
     */
    changeActiveTab(activeTab) {
        const {
            [activeTab]: {
                url,
                isFullUrl = false
            }
        } = this.tabsFilterEnabled(MyAccountContainer.tabMap);

        if (isFullUrl) {
            history.push(appendWithStoreCode(url));
        } else {
            history.push(appendWithStoreCode(`${ ACCOUNT_URL }${ url }`));
        }

        this.changeMyAccountHeaderState();
    }

    /**
     * Override: prevent breadcrumbs update on mobile (because it will display it) + fix static method call
     */
    updateBreadcrumbs() {
        const { isMobile, updateBreadcrumbs } = this.props;
        const { activeTab } = this.state;
        const { url, tabName, isFullUrl } = MyAccountContainer.tabMap[activeTab];
        const breadcrumbs = [];

        if (isMobile) {
            return;
        }

        if (activeTab !== MY_ACCOUNT) {
            breadcrumbs.push({
                url: isFullUrl ? url : `${ ACCOUNT_URL }${ url }`,
                name: tabName
            });
        }

        breadcrumbs.push({ name: __('My Account'), url: ACCOUNT_URL });

        updateBreadcrumbs(breadcrumbs);
    }

    /**
     * Override
     * @returns {{isMobile}}
     */
    containerProps() {
        const { isMobile, customer } = this.props;
        const { isLoadingOrders } = this.state;

        return {
            ...super.containerProps(),
            isMobile,
            customer,
            isLoading: isLoadingOrders
        };
    }

    onBackClick() {
        history.goBack();
    }

    /**
     * Override: remove notification after redirect
     */
    redirectIfNotSignedIn() {
        const {
            baseLinkUrl,
            location: { pathname = '' },
            customer: { group_id = 0 } = {}
        } = this.props;

        if (isSignedIn()) {
            // Quick order page => redirect non pro/etu user to dashboard
            if (pathname.includes(ACCOUNT_QUICK_ORDER_URL) && ![GROUP_ID_PRO, GROUP_ID_STUDENT, GROUP_ID_EMPLOYEE].includes(group_id)) {
                history.replace({ pathname: ACCOUNT_URL });
            }

            // Other pages => do nothing for signed-in users
            return;
        }

        if (this.handleCheckIfSelectedTab()) { // do redirect if it is customer url
            history.replace({ pathname: ACCOUNT_LOGIN_URL });
        }

        const path = baseLinkUrl
            ? appendWithStoreCode(ACCOUNT_LOGIN_URL)
            : replace(/\/customer\/account\/?.*/i, ACCOUNT_LOGIN_URL);

        history.replace({ pathname: path });
    }

    /**
     * Override: duplicate of parent method because of static method call
     * @returns {JSX.Element}
     */
    render() {
        return (
            <MyAccount
              { ...this.containerProps() }
              { ...this.containerFunctions }
              tabMap={ this.tabsFilterEnabled(MyAccountContainer.tabMap) }
            />
        );
    }
}

export default withRouter(withReducers({
    OrderReducer
})(connect(mapStateToProps, mapDispatchToProps)(MyAccountContainer)));
